import { type FocusEventHandler, forwardRef, type ReactNode, useState } from "react";

import { type SrpProductData } from "~/utils/models";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import Parameter from "~/common/components/Parameter";
import { Autocomplete, type AutocompleteProps } from "~/common/components/Select/AutocompleteSelect";
import { useProductSuggestions } from "~/features/orders/common/hooks/useProductSuggestions";
import { useOptions } from "~/common/components/Select/SimpleAutocompleteSelect";
import { type ProductData } from "~/domains/product/types/ProductData";
import { C_DROPDOWN_HOVER, C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { useTranslation, type WithTranslation } from "react-i18next";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { Translation } from "~/app/i18n/Translation";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { type ColorVariants, type TextFieldVariants } from "~/common/types";
import { Box, type SxProps, type Theme } from "~/common/mui";

interface Props {
  value: string;
  onChange: (product: ProductData, inputValue: string) => void;
  label?: ReactNode;
  onFocus?: FocusEventHandler<HTMLDivElement>;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  classes?: AutocompleteProps<any, any, any, any>["classes"];
  autoFocus?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  disableNewVerLink?: boolean;
  disabled?: boolean;
  variant?: TextFieldVariants;
  color?: ColorVariants;
  shrink?: boolean;
  startAdornment?: ReactNode;
  size?: "small" | "medium";
}

const PreviewTableMaterialIdInput = forwardRef<HTMLInputElement, Props>(
  ({ onChange, autoFocus, onBlur, classes: outerClasses, disableNewVerLink, startAdornment, ...rest }, ref) => {
    const { t } = useTranslation();

    const [inputValue, setInputValue] = useState(rest.value);

    const { suggestions, loading, isOpen, open, close } = useProductSuggestions(inputValue);

    const { options, optionMap } = useOptions(suggestions, (item) => item.designation);

    return (
      <Autocomplete
        {...rest}
        classes={outerClasses}
        slotProps={{
          paper: { sx: previewTableAutosuggestSx.paper },
          listbox: { sx: previewTableAutosuggestSx.listbox },
        }}
        data-test-id={"autocomplete-designation"}
        open={isOpen}
        onOpen={open}
        inputValue={inputValue}
        onInputChange={(e, value) => setInputValue(value)}
        onChange={(e, value) => onChange(optionMap[value], inputValue)}
        onBlur={(e) => {
          setInputValue(rest.value);
          onBlur?.(e);
        }}
        onClose={close}
        options={options}
        loading={loading}
        freeSolo={true}
        loadingText={t("spinner.text")}
        disableClearable={true}
        autoFocus={autoFocus}
        ref={ref}
        startAdornment={startAdornment}
        filterOptions={(option: string[]) => option} // just pass through all the options --- assume proper filtering is done in BE
        renderOption={makeRenderOption(optionMap, previewTableAutosuggestSx.option, t, disableNewVerLink)}
      />
    );
  },
);
export default PreviewTableMaterialIdInput;

export const previewTableAutosuggestSx = {
  option: {
    background: "none !important",
    padding: "8px 16px",
    width: "100%",
    "&.Mui-focused ": {
      backgroundColor: `${C_DROPDOWN_HOVER} !important`,
    },
    '&[data-focus="true"]': {
      backgroundColor: `${C_DROPDOWN_HOVER} !important`,
    },
  },
  paper: {
    boxShadow: "none",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderRadius: "0px",
    margin: "0",
  },
  listbox: {
    padding: "0",
  },
};

export const makeRenderOption =
  (optionMap: Dict<SrpProductData>, optionSx: SxProps<Theme>, t: WithTranslation["t"], disableNewVerLink?: boolean) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, designation: string) => {
    const entry = optionMap[designation];
    return (
      <li {...props}>
        <Box data-test-id={`autocomplete-designation-item`} data-test-designation={designation} sx={optionSx}>
          <ProductDesignation size={"label-large"} designation={entry.designation} isPreferred={entry.preferred} />
          <p className="mt-1 mb-0 label-large f-carbon">
            <Translation {...entry.shortDescription} />
          </p>

          <NewVersionMessage className={"mt-2"} baseProduct={entry} disableLink={disableNewVerLink} />

          {entry.oldDesignation && (
            <Parameter className={"mt-2"} label={t("productDetailPage.oldDesignation")} value={entry.oldDesignation} />
          )}
          {entry.isPMA && <InfoProductMessagePMA className={"mt-2"} short={true} />}
        </Box>
      </li>
    );
  };
